/**
 * MoisturePage — `/pulse/moisture`.
 *
 * Detail screen for the Moisture card on the main TurfPulse dashboard.
 * Shows live moisture %, the 48h sparkline, target/ETA tiles, and a
 * data-driven recommendation tied to the bermuda 45-50% mow-friendly band.
 */
import React, { useEffect, useState } from 'react';
import { Droplets, Lightbulb, Brain, CloudRain, Users } from 'lucide-react';
import { PulseDetailLayout, PulseTile, PulseInsight } from '../../components/pulse/PulseDetailLayout';
import { PulseSparkline } from '../../components/pulse/PulseSparkline';
import { PerChannelSparkline } from '../../components/pulse/PerChannelSparkline';
import { API_URL } from '../../config/api';

function statusFor(v) {
  if (v == null) return { text: '—', color: 'text-slate-400' };
  if (v < 30) return { text: 'Critical Dry', color: 'text-rose-400' };
  if (v < 40) return { text: 'Dry',          color: 'text-amber-500' };
  if (v < 50) return { text: 'Low',          color: 'text-amber-400' };
  if (v < 60) return { text: 'Medium',       color: 'text-yellow-400' };
  if (v < 70) return { text: 'High',         color: 'text-emerald-400' };
  return { text: 'Saturated', color: 'text-sky-400' };
}

export default function MoisturePage() {
  const [now, setNow] = useState(null);
  const [history, setHistory] = useState(null);
  const [perProbe, setPerProbe] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/dashboard/now`,  { credentials: 'include' })
      .then(r => r.json()).then(setNow).catch(() => {});
    fetch(`${API_URL}/api/dashboard/history?metric=moisture&hours=48`, { credentials: 'include' })
      .then(r => r.json()).then(setHistory).catch(() => {});
  }, []);

  const value = now?.metrics?.moisture?.value;
  const status = statusFor(value);

  // Drying-rate ETA: linear regress over last 24h, project to 48%.
  let etaHours = null;
  if (history?.series && history.series.length > 2 && value != null) {
    const recent = history.series.slice(-24); // last ~24 readings
    const first = recent[0]; const last = recent[recent.length - 1];
    const dt = (new Date(last.t) - new Date(first.t)) / 3.6e6;
    const dv = last.v - first.v;
    const rate = dt > 0 ? dv / dt : 0;
    if (rate < 0 && value > 48) {
      etaHours = Math.round((48 - value) / rate); // negative rate; positive hours
    } else if (rate > 0 && value < 48) {
      etaHours = Math.round((48 - value) / rate);
    }
  }

  return (
    <PulseDetailLayout
      title="Moisture"
      icon={Droplets}
      iconColor="text-sky-400"
      bigValue={value != null ? `${value}%` : null}
      bigValueColor="text-sky-400"
      badgeText={status.text}
      badgeColor={status.color}
    >
      <div className="flex items-center justify-between pl-1">
        <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
          Soil Moisture · 48h
          {now?.metrics?.moisture?.probe_count > 1 && (
            <span className="ml-2 normal-case tracking-normal text-slate-500"
                  data-testid="moisture-probe-context">
              avg of {now.metrics.moisture.probe_count} probes
              {now.metrics.moisture.range && (
                <> · {now.metrics.moisture.range.min}–{now.metrics.moisture.range.max}%</>
              )}
            </span>
          )}
        </div>
        {now?.metrics?.moisture?.probe_count > 1 && (
          <button
            data-testid="moisture-per-probe-toggle"
            onClick={() => setPerProbe(v => !v)}
            className="text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 text-sky-400 hover:text-sky-300"
          >
            <Users className="w-3 h-3" />
            {perProbe ? 'Average' : 'Per probe'}
          </button>
        )}
      </div>
      {perProbe
        ? <PerChannelSparkline metric="moisture" hours={48} unit="%" idealMin={45} idealMax={50} height={240} />
        : <PulseSparkline metric="moisture" hours={48} idealMin={45} idealMax={50} unit="%" />}

      <div className="grid grid-cols-2 gap-3">
        <PulseTile
          testid="moisture-target-tile"
          label="Target"
          value="48%"
          valueColor="text-emerald-400"
          subline="Ideal in range"
        />
        <PulseTile
          testid="moisture-eta-tile"
          label="Estimated"
          value={etaHours != null ? `~${Math.abs(etaHours)} hours` : '—'}
          valueColor="text-slate-100"
          subline={etaHours != null ? 'to reach 48%' : 'Need more history'}
        />
      </div>

      <PulseInsight tone="sky" icon={Brain} label="Insight" testid="moisture-insight">
        {history?.trend === 'falling'
          ? 'Soil is drying at a steady rate. Ideal mowing conditions approaching.'
          : history?.trend === 'rising'
          ? 'Soil is wetting up — recent rain or irrigation cycle in play.'
          : 'Moisture is holding steady — wait for VPD to draw it down.'}
      </PulseInsight>

      <PulseInsight tone="emerald" icon={Lightbulb} label="Recommendation" testid="moisture-recommendation">
        {value == null
          ? 'Waiting on sensor reading.'
          : value > 60
            ? 'Wait — soil profile is full. Skip irrigation; let plant pull down before any input.'
            : value > 50
              ? 'Hold — let it drop into the 45–50% mow window before cutting.'
              : value >= 45
                ? 'Mow now — moisture is in the ideal 45–50% window for a clean cut.'
                : value >= 35
                  ? 'Irrigate to MAX target — soil dropping below comfort band.'
                  : 'Irrigate immediately — soil at critical dry, plant stress imminent.'}
      </PulseInsight>

      {/* Dual Rain Gauge panel — only renders for users with BOTH gauges.
          The elevated gauge drives skip logic; the ground gauge verifies
          sprinkler output. Showing the delta makes the "did my sprinklers
          actually hit the lawn?" question visible at a glance. */}
      {now?.water_load?.has_dual_gauge && (
        <div className="bg-slate-900/80 border border-emerald-900/40 rounded-2xl px-4 py-3"
             data-testid="dual-rain-gauge-panel">
          <div className="flex items-center gap-2 mb-2">
            <CloudRain className="w-4 h-4 text-sky-400" />
            <span className="text-[11px] uppercase tracking-wider font-semibold text-emerald-400">
              Dual Rain Gauge · last 24h
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div data-testid="rain-gauge-elevated">
              <div className="text-[10px] uppercase text-slate-500 font-semibold">Elevated (skips)</div>
              <div className="text-2xl font-bold text-sky-400">
                {(now.water_load.rain_24h_in_elevated ?? 0).toFixed(2)}"
              </div>
              <div className="text-[10px] text-slate-400">Piezo · clean precip</div>
            </div>
            <div data-testid="rain-gauge-ground">
              <div className="text-[10px] uppercase text-slate-500 font-semibold">Ground (verify)</div>
              <div className="text-2xl font-bold text-emerald-400">
                {(now.water_load.rain_24h_in_ground ?? 0).toFixed(2)}"
              </div>
              <div className="text-[10px] text-slate-400">Tipping · catches sprinklers</div>
            </div>
          </div>
          {(() => {
            const elev = now.water_load.rain_24h_in_elevated || 0;
            const grnd = now.water_load.rain_24h_in_ground   || 0;
            const delta = +(grnd - elev).toFixed(2);
            if (Math.abs(delta) < 0.05) return null;
            return (
              <div className="text-[11px] text-slate-300 mt-2 pt-2 border-t border-slate-800"
                   data-testid="rain-gauge-delta">
                Δ {delta > 0 ? '+' : ''}{delta}" — {delta > 0
                  ? 'sprinklers contributed to ground gauge (output verified)'
                  : 'elevated saw more rain than ground (mist / wind drift)'}
              </div>
            );
          })()}
        </div>
      )}
    </PulseDetailLayout>
  );
}
